/**
 * With Transpose & Warp enabled, the plugin drags a re-rendered copy of a
 * sample rather than the catalog file, and encodes what it changed in the
 * filename before the extension (see `modifiedPreviewUserPath` in the platform
 * repo, `surfaces/libs/plugin/shared/util/src/lib/file-utils.ts`):
 *
 * - `_140bpm` / `_120_400bpm` — the tempo the audio was rendered to
 * - `_2x` / `_0_500x` — a timestretch multiplier applied on top of that tempo
 * - `_2steps` / `_-3steps` — a pitch shift in semitones
 *
 * The rendered file is the audio we actually hold, so those suffixes outrank
 * the catalog asset they came from: the suffix tempo is the real tempo, and a
 * pitch shift means the catalog key no longer describes the sound.
 */
export interface ModifiedFileName {
    /** The filename with any render suffixes peeled off. */
    name: string;
    /** Tempo the plugin rendered the audio to, from a `_<n>bpm` suffix. */
    bpm: number | undefined;
    /** A `_<n>steps` suffix was present, so the catalog key is stale. */
    wasPitchShifted: boolean;
}

/** `120` stays `120`; the plugin writes fractional tempos as `120_400`. */
const parseSuffixNumber = (raw: string): number | undefined => {
    const value = Number(raw.replace("_", "."));
    return Number.isFinite(value) ? value : undefined;
};

const kBpmSuffix = /_(\d+(?:_\d{1,3})?)bpm$/;
const kStretchSuffix = /_(\d+(?:_\d{1,3})?)x$/;
const kPitchSuffix = /_(-?\d+)steps$/;

interface PeeledSuffix {
    stem: string;
    bpm: number | undefined;
    wasPitchShifted: boolean;
}

/** Remove the outermost render suffix from `stem`, or `undefined` if it has none. */
const peelSuffix = (stem: string): PeeledSuffix | undefined => {
    const pitch = kPitchSuffix.exec(stem);
    if (pitch !== null) {
        return {
            stem: stem.slice(0, pitch.index),
            bpm: undefined,
            wasPitchShifted: true,
        };
    }
    const stretch = kStretchSuffix.exec(stem);
    if (stretch !== null) {
        return {
            stem: stem.slice(0, stretch.index),
            bpm: undefined,
            wasPitchShifted: false,
        };
    }
    const bpm = kBpmSuffix.exec(stem);
    if (bpm !== null) {
        return {
            stem: stem.slice(0, bpm.index),
            bpm: parseSuffixNumber(bpm[1]),
            wasPitchShifted: false,
        };
    }
    return undefined;
};

const splitExtension = (fileName: string): { stem: string; extension: string } => {
    const dot = fileName.lastIndexOf(".");
    if (dot <= 0) {
        return { stem: fileName, extension: "" };
    }
    return { stem: fileName.slice(0, dot), extension: fileName.slice(dot) };
};

/**
 * Every reading of `fileName`, from "the plugin added nothing" through to fully
 * peeled, so a caller can take the first one that matches a known asset.
 *
 * Peeling has to stop at the first match rather than run to the end: plenty of
 * catalog samples are named `..._128bpm.wav` in the first place, and a warped
 * copy of one arrives as `..._128bpm_140bpm.wav`. Reading the outermost suffix
 * only gives 140 — the tempo of the audio in hand — where peeling everything
 * would report 128 and match nothing.
 */
export const modifiedFileNameCandidates = (fileName: string): ModifiedFileName[] => {
    const { stem, extension } = splitExtension(fileName);

    const candidates: ModifiedFileName[] = [
        { name: fileName, bpm: undefined, wasPitchShifted: false },
    ];

    let remaining = stem;
    let bpm: number | undefined;
    let wasPitchShifted = false;

    let peeled = peelSuffix(remaining);
    while (peeled !== undefined) {
        remaining = peeled.stem;
        // Peeling runs backwards through the suffixes, so the outermost tempo
        // is the first one reached — and it is the one the audio was rendered
        // to, even when the catalog name carries a tempo of its own.
        bpm ??= peeled.bpm;
        wasPitchShifted ||= peeled.wasPitchShifted;
        candidates.push({ name: `${remaining}${extension}`, bpm, wasPitchShifted });
        peeled = peelSuffix(remaining);
    }

    return candidates;
};

/** The reading of `fileName` with every render suffix peeled off. */
export const parseModifiedFileName = (fileName: string): ModifiedFileName => {
    const candidates = modifiedFileNameCandidates(fileName);
    return candidates[candidates.length - 1];
};
